import {
  catalogEventKindOf,
  type AdminCatalogChange,
  type CatalogEventKind,
} from '@steado/admin-contract/catalog-history';

/**
 * How History reads: `catalog_changes` rows turned into the events and field
 * rows the console shows, and which of them Revert can put back.
 *
 * A row in `catalog_changes` is a database row before and after. An operator
 * thinks in fields ("Energy per 100 g, 98.4 kcal to 221 kcal") and in the
 * actions that made them ("Edited in the console"), so this groups rows by the
 * batch id their writer stated and splits each update into one line per
 * column that moved.
 *
 * Revert is offered per field, per nutrition block and per event, and refused
 * here, before the server would, for the two reasons the server would give:
 * the field has changed since (newer wins), or it belongs to the nutrition
 * block, which goes back whole. Pure, so both are unit-tested.
 */

export type Json = Record<string, unknown>;

/** The nutrition block, which reverts together or not at all. */
export const NUTRITION_COLUMNS = [
  'kcal_per_100g',
  'protein_g_per_100g',
  'carbs_g_per_100g',
  'fat_g_per_100g',
  'fibre_g_per_100g',
] as const;

/** Columns that move on their own or are derived from others: never a line in History. */
const HIDDEN_COLUMNS = new Set([
  'id',
  'updated_at',
  'created_at',
  'revision',
  'protein_band',
  'name_normalised',
  'alias_normalised',
  'embedding',
  'search_vector',
  'sort_order',
  'version',
  'food_id',
  'exercise_id',
  'dish_id',
  'from_exercise_id',
  'curated',
]);

type ValueFormat = 'text' | 'enum' | 'list' | 'bool' | 'kcal' | 'grams' | 'months' | 'portions';

interface ColumnSpec {
  readonly label: string;
  /** What a Revert button names, in lower case. */
  readonly short: string;
  readonly format: ValueFormat;
  readonly group: FieldGroup;
}

/** What the History filter groups fields into. */
export type FieldGroup = 'nutrition' | 'portions' | 'names' | 'allergens' | 'other';

const FOOD_COLUMNS: Readonly<Record<string, ColumnSpec>> = {
  kcal_per_100g: { label: 'Energy per 100 g', short: 'energy', format: 'kcal', group: 'nutrition' },
  protein_g_per_100g: { label: 'Protein per 100 g', short: 'protein', format: 'grams', group: 'nutrition' },
  carbs_g_per_100g: {
    label: 'Carbohydrate per 100 g',
    short: 'carbohydrate',
    format: 'grams',
    group: 'nutrition',
  },
  fat_g_per_100g: { label: 'Fat per 100 g', short: 'fat', format: 'grams', group: 'nutrition' },
  fibre_g_per_100g: { label: 'Fibre per 100 g', short: 'fibre', format: 'grams', group: 'nutrition' },
  display_name: { label: 'Display name', short: 'display name', format: 'text', group: 'names' },
  name: { label: 'Name', short: 'name', format: 'text', group: 'names' },
  allergens: { label: 'Allergens', short: 'allergens', format: 'list', group: 'allergens' },
  allergen_source: {
    label: 'Allergens checked by',
    short: 'allergen source',
    format: 'enum',
    group: 'allergens',
  },
  diet_class: { label: 'Diet class', short: 'diet class', format: 'enum', group: 'other' },
  diet_class_source: {
    label: 'Diet class checked by',
    short: 'diet class source',
    format: 'enum',
    group: 'other',
  },
  has_onion_garlic: {
    label: 'Contains onion or garlic',
    short: 'onion or garlic',
    format: 'bool',
    group: 'other',
  },
  jain_excluded: { label: 'Jain avoids', short: 'Jain', format: 'bool', group: 'other' },
  has_beef: { label: 'Contains beef', short: 'beef', format: 'bool', group: 'other' },
  servable_as_is: { label: 'Eaten as it is', short: 'eaten as it is', format: 'bool', group: 'other' },
  is_common_in_india: {
    label: 'Everyday food in India',
    short: 'everyday food in India',
    format: 'bool',
    group: 'other',
  },
  cuisines: { label: 'Cuisines', short: 'cuisines', format: 'list', group: 'other' },
  meal_role: { label: 'Meal role', short: 'meal role', format: 'enum', group: 'other' },
  prep_time_band: { label: 'Prep time', short: 'prep time', format: 'enum', group: 'other' },
  cost_band: { label: 'Cost', short: 'cost', format: 'enum', group: 'other' },
  health_tier: { label: 'Health tier', short: 'health tier', format: 'enum', group: 'other' },
  season_months: { label: 'Months in season', short: 'months in season', format: 'months', group: 'other' },
  meal_times: { label: 'Meal times', short: 'meal times', format: 'list', group: 'other' },
  serving_min_portions: {
    label: 'Serving, fewest portions',
    short: 'serving',
    format: 'text',
    group: 'portions',
  },
  serving_typical_portions: {
    label: 'Serving, typical portions',
    short: 'serving',
    format: 'text',
    group: 'portions',
  },
  serving_max_portions: {
    label: 'Serving, most portions',
    short: 'serving',
    format: 'text',
    group: 'portions',
  },
  serving_max_day_portions: {
    label: 'Serving, most in a day',
    short: 'serving',
    format: 'text',
    group: 'portions',
  },
  serving_elastic: { label: 'Serving, can stretch', short: 'serving', format: 'bool', group: 'portions' },
  is_active: { label: 'Active', short: 'active', format: 'bool', group: 'other' },
  is_verified: { label: 'Verified', short: 'verified', format: 'bool', group: 'other' },
  category: { label: 'Category', short: 'category', format: 'text', group: 'other' },
};

const EXERCISE_COLUMNS: Readonly<Record<string, ColumnSpec>> = {
  name: { label: 'Name', short: 'name', format: 'text', group: 'names' },
  how_to_steps: { label: 'How to do it', short: 'steps', format: 'list', group: 'other' },
  primary_muscles: { label: 'Primary muscles', short: 'primary muscles', format: 'list', group: 'other' },
  secondary_muscles: {
    label: 'Secondary muscles',
    short: 'secondary muscles',
    format: 'list',
    group: 'other',
  },
  equipment: { label: 'Equipment', short: 'equipment', format: 'list', group: 'other' },
  contraindications: { label: 'Avoid with', short: 'avoid with', format: 'list', group: 'other' },
  category: { label: 'Category', short: 'category', format: 'enum', group: 'other' },
  difficulty: { label: 'Difficulty', short: 'difficulty', format: 'enum', group: 'other' },
  demo_video_key: { label: 'Demo clip', short: 'demo clip', format: 'text', group: 'other' },
  image_keys: { label: 'Stills', short: 'stills', format: 'list', group: 'other' },
  is_active: { label: 'Active', short: 'active', format: 'bool', group: 'other' },
  is_weighted: { label: 'Weighted', short: 'weighted', format: 'bool', group: 'other' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A JSON value from a row, as text: strings as they are, anything else as JSON. */
export function text(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function sentenceCase(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);
}

function wireWord(value: unknown): string {
  return text(value).toLowerCase().replace(/_/g, ' ');
}

/** Grams to one decimal, or two when the figure has them; energy as stated. */
function number(value: unknown, digits: number): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return text(value);
  if (digits === 0) return String(Math.round(n * 100) / 100);
  // At least one decimal, as the design writes grams ("24.0 g"), and the second when it is there ("0.12 g").
  return Number.isInteger(Math.round(n * 100) / 10) ? n.toFixed(digits) : n.toFixed(2);
}

/** A value as a person reads it. Absent is "none", never an empty cell. */
export function formatValue(value: unknown, format: ValueFormat): string {
  if (value === null || value === undefined) return 'none';
  switch (format) {
    case 'bool':
      return value === true ? 'Yes' : 'No';
    case 'kcal':
      return `${number(value, 0)} kcal`;
    case 'grams':
      return `${number(value, 1)} g`;
    case 'enum':
      return sentenceCase(wireWord(value));
    case 'months': {
      const months = Array.isArray(value) ? value : [];
      return months.length === 0 ? 'none' : months.map((m) => MONTHS[Number(m) - 1] ?? String(m)).join(', ');
    }
    case 'list': {
      const items = Array.isArray(value) ? value.map((item) => wireWord(item)) : [];
      return items.length === 0 ? 'none' : sentenceCase(items.join(', '));
    }
    case 'portions':
    case 'text':
      return typeof value === 'string' ? value : JSON.stringify(value);
  }
}

/** What reverting one line sends. */
export interface RevertTarget {
  readonly changeId: string;
  /** Columns of an update, as `changedFields` names them; absent reverts the whole change. */
  readonly fields?: readonly string[];
}

/** One line of History: a field, before and after. */
export interface HistoryRow {
  readonly key: string;
  readonly field: string;
  readonly short: string;
  readonly before: string;
  readonly after: string;
  readonly group: FieldGroup;
  /** Every change the line stands for, oldest first. */
  readonly changes: readonly AdminCatalogChange[];
  /** The columns it covers, for "changed since". Empty for a whole row (a portion, a name). */
  readonly columns: readonly string[];
  readonly isNutrition: boolean;
  /** A food or exercise added: never reverted from History. */
  readonly isCreation: boolean;
  readonly target: RevertTarget;
}

/** One action's changes: a save, an approval, a batch, a revert. */
export interface HistoryEvent {
  readonly key: string;
  readonly batchId: string | null;
  readonly kind: CatalogEventKind;
  readonly source: string;
  readonly actor: AdminCatalogChange['actor'];
  readonly note: string | null;
  readonly sources: readonly string[];
  readonly at: string;
  readonly changes: readonly AdminCatalogChange[];
  readonly rows: readonly HistoryRow[];
}

export function eventKeyOf(change: AdminCatalogChange): string {
  return change.batchId ?? `change:${change.id}`;
}

function compareIds(a: string, b: string): number {
  const x = BigInt(a);
  const y = BigInt(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

/**
 * Changes, any order, grouped into events newest first, each event's rows in
 * the order its writer made them.
 */
export function groupEvents(
  changes: readonly AdminCatalogChange[],
  subject: 'food' | 'exercise',
): HistoryEvent[] {
  const byKey = new Map<string, AdminCatalogChange[]>();
  for (const change of [...changes].sort((a, b) => compareIds(a.id, b.id))) {
    const key = eventKeyOf(change);
    byKey.set(key, [...(byKey.get(key) ?? []), change]);
  }
  const events = [...byKey.entries()].flatMap(([key, list]): HistoryEvent[] => {
    const first = list[0];
    if (first === undefined) return [];
    const last = list.at(-1) ?? first;
    const event: HistoryEvent = {
      key,
      batchId: first.batchId,
      kind: catalogEventKindOf(first.source),
      source: first.source,
      actor: first.actor,
      note: list.find((change) => change.note !== null)?.note ?? null,
      sources: list.find((change) => change.sources !== null && change.sources.length > 0)?.sources ?? [],
      at: last.createdAt,
      changes: list,
      rows: rowsOf(list, subject),
    };
    return [event];
  });
  return events.sort((a, b) => compareIds(b.changes.at(-1)?.id ?? '0', a.changes.at(-1)?.id ?? '0'));
}

function portionName(record: Json | null): string {
  return `1 ${text(record?.label, 'portion')}`;
}

function portionGrams(record: Json | null): string {
  return record?.grams === undefined ? 'none' : `${number(record.grams, 0)} g`;
}

/** The lines one event's changes read as. */
export function rowsOf(changes: readonly AdminCatalogChange[], subject: 'food' | 'exercise'): HistoryRow[] {
  const rows: HistoryRow[] = [];
  const aliasChanges = changes.filter(
    (c) => c.tableName === 'food_aliases' || c.tableName === 'exercise_aliases',
  );
  const ownTable = subject === 'food' ? 'foods' : 'exercises';
  const columns = subject === 'food' ? FOOD_COLUMNS : EXERCISE_COLUMNS;

  for (const change of changes) {
    if (change.tableName === ownTable) {
      if (change.op === 'insert') {
        const name = text(change.record?.display_name ?? change.record?.name, 'It');
        rows.push({
          key: `${change.id}:insert`,
          field: name,
          short: name,
          before: 'not in the catalog',
          after: `added with ${String(change.changedFields.filter((f) => !HIDDEN_COLUMNS.has(f)).length)} fields`,
          group: 'other',
          changes: [change],
          columns: [],
          isNutrition: false,
          isCreation: true,
          target: { changeId: change.id },
        });
        continue;
      }
      if (change.op === 'delete') {
        rows.push({
          key: `${change.id}:delete`,
          field: text(change.oldRecord?.name, 'Row'),
          short: 'removal',
          before: 'in the catalog',
          after: 'removed',
          group: 'other',
          changes: [change],
          columns: [],
          isNutrition: false,
          isCreation: false,
          target: { changeId: change.id },
        });
        continue;
      }
      for (const column of change.changedFields) {
        if (HIDDEN_COLUMNS.has(column)) continue;
        const spec = columns[column] ?? {
          label: sentenceCase(wireWord(column)),
          short: wireWord(column),
          format: 'text' as const,
          group: 'other' as const,
        };
        const isNutrition = (NUTRITION_COLUMNS as readonly string[]).includes(column);
        rows.push({
          key: `${change.id}:${column}`,
          field: spec.label,
          short: spec.short,
          before: formatValue(change.oldRecord?.[column], spec.format),
          after: formatValue(change.record?.[column], spec.format),
          group: spec.group,
          changes: [change],
          columns: isNutrition ? [...NUTRITION_COLUMNS] : [column],
          isNutrition,
          isCreation: false,
          target: { changeId: change.id, fields: [column] },
        });
      }
      continue;
    }

    if (change.tableName === 'food_portions') {
      rows.push(...portionRows(change));
      continue;
    }

    if (change.tableName === 'food_aliases' || change.tableName === 'exercise_aliases') continue;

    if (change.tableName === 'exercise_edges') {
      const record = change.record ?? change.oldRecord;
      const relation = sentenceCase(wireWord(record?.relation ?? 'alternative'));
      rows.push({
        key: `${change.id}:edge`,
        field: `Alternative, ${relation.toLowerCase()}`,
        short: 'alternative',
        before:
          change.op === 'insert'
            ? 'none'
            : `${number(Number(change.oldRecord?.stimulus_match ?? 0) * 100, 0)}% match`,
        after:
          change.op === 'delete'
            ? 'none'
            : `${number(Number(change.record?.stimulus_match ?? 0) * 100, 0)}% match`,
        group: 'other',
        changes: [change],
        columns: [],
        isNutrition: false,
        isCreation: false,
        target: { changeId: change.id },
      });
      continue;
    }

    if (change.tableName === 'food_recipes' || change.tableName === 'food_ingredients') {
      const isLine = change.tableName === 'food_ingredients';
      const record = change.record ?? change.oldRecord;
      rows.push({
        key: `${change.id}:recipe`,
        field: isLine ? `Recipe line, ${text(record?.raw_name)}` : 'Recipe',
        short: 'recipe',
        before:
          change.op === 'insert'
            ? 'none'
            : isLine
              ? portionGrams(change.oldRecord)
              : `version ${text(change.oldRecord?.version)}, ${wireWord(change.oldRecord?.status ?? '')}`,
        after:
          change.op === 'delete'
            ? 'none'
            : isLine
              ? portionGrams(change.record)
              : `version ${text(change.record?.version)}, ${wireWord(change.record?.status ?? '')}`,
        group: 'other',
        changes: [change],
        columns: [],
        isNutrition: false,
        isCreation: false,
        target: { changeId: change.id },
      });
    }
  }

  const firstAlias = aliasChanges[0];
  if (firstAlias !== undefined) {
    const first = firstAlias;
    const added = aliasChanges.filter((c) => c.op === 'insert').map((c) => text(c.record?.alias));
    const removed = aliasChanges.filter((c) => c.op === 'delete').map((c) => text(c.oldRecord?.alias));
    rows.push({
      key: `${first.id}:aliases`,
      field: 'Other names',
      short: 'other names',
      before: removed.length === 0 ? 'none' : removed.join(', '),
      after: added.length === 0 ? 'none' : added.join(', '),
      group: 'names',
      changes: aliasChanges,
      columns: [],
      isNutrition: false,
      isCreation: false,
      target: { changeId: first.id },
    });
  }
  return rows;
}

function portionRows(change: AdminCatalogChange): HistoryRow[] {
  const base = {
    group: 'portions' as const,
    changes: [change],
    columns: [] as string[],
    isNutrition: false,
    isCreation: false,
    target: { changeId: change.id },
  };
  if (change.op === 'insert') {
    return [
      {
        ...base,
        key: `${change.id}:portion`,
        field: `Portion added, ${portionName(change.record)}`,
        short: 'portion',
        before: 'none',
        after: portionGrams(change.record),
      },
    ];
  }
  if (change.op === 'delete') {
    return [
      {
        ...base,
        key: `${change.id}:portion`,
        field: `Portion removed, ${portionName(change.oldRecord)}`,
        short: 'portion',
        before: portionGrams(change.oldRecord),
        after: 'none',
      },
    ];
  }
  const rows: HistoryRow[] = [];
  const isDefault = change.record?.is_default === true;
  const name = portionName(change.record);
  for (const column of change.changedFields) {
    if (column === 'grams') {
      rows.push({
        ...base,
        key: `${change.id}:grams`,
        field: `${isDefault ? 'Default portion' : 'Portion'}, ${name}`,
        short: 'portion',
        before: portionGrams(change.oldRecord),
        after: portionGrams(change.record),
      });
    } else if (column === 'label') {
      rows.push({
        ...base,
        key: `${change.id}:label`,
        field: 'Portion name',
        short: 'portion name',
        before: portionName(change.oldRecord),
        after: name,
      });
    } else if (column === 'unit') {
      rows.push({
        ...base,
        key: `${change.id}:unit`,
        field: `Portion unit, ${name}`,
        short: 'portion unit',
        before: formatValue(change.oldRecord?.unit, 'enum'),
        after: formatValue(change.record?.unit, 'enum'),
      });
    } else if (column === 'is_default' && isDefault) {
      rows.push({
        ...base,
        key: `${change.id}:default`,
        field: 'Default portion',
        short: 'default portion',
        before: 'another portion',
        after: name,
      });
    }
  }
  return rows;
}

// --- revert ---------------------------------------------------------------------

/** Why Revert is not offered on a line, or null when it is. */
export interface RowState {
  readonly reason: string | null;
  /** A hard block (changed since, an addition), as opposed to "revert the block instead". */
  readonly blocked: boolean;
}

/** "today at 11:42", "yesterday at 09:10", "on 3 Oct": when, inside a sentence. */
export function inlineWhen(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diff = Math.round((day(now) - day(at)) / 86_400_000);
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (diff === 0) return `today at ${time}`;
  if (diff === 1) return `yesterday at ${time}`;
  const year = at.getFullYear() === now.getFullYear() ? '' : ` ${String(at.getFullYear())}`;
  return `on ${String(at.getDate())} ${MONTHS[at.getMonth()] ?? ''}${year}`;
}

/** "Today 11:42", "Yesterday 15:02", "3 Oct 12:30": when, as a label. */
export function labelWhen(iso: string, now: Date = new Date()): string {
  const inline = inlineWhen(iso, now);
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (inline.startsWith('today')) return `Today ${time}`;
  if (inline.startsWith('yesterday')) return `Yesterday ${time}`;
  return `${inline.slice('on '.length)} ${time}`;
}

/**
 * The newest change after `row`'s that touched what it covers: the same
 * columns of the same row, or for a portion, a name or an edge, the same row
 * at all.
 */
function newerTouch(row: HistoryRow, history: readonly AdminCatalogChange[]): AdminCatalogChange | null {
  const newest = row.changes.at(-1);
  if (newest === undefined) return null;
  const rowIds = new Set(row.changes.map((change) => `${change.tableName}|${change.rowId}`));
  let found: AdminCatalogChange | null = null;
  for (const change of history) {
    if (compareIds(change.id, newest.id) <= 0) continue;
    if (!rowIds.has(`${change.tableName}|${change.rowId}`)) continue;
    if (row.columns.length > 0 && !change.changedFields.some((field) => row.columns.includes(field)))
      continue;
    if (found === null || compareIds(change.id, found.id) > 0) found = change;
  }
  return found;
}

/**
 * Whether each line of an event can be reverted on its own, and if not, why:
 * in the order the server would refuse it.
 */
export function rowStates(
  event: HistoryEvent,
  history: readonly AdminCatalogChange[],
  subject: 'food' | 'exercise',
  now: Date = new Date(),
): Map<string, RowState> {
  const states = new Map<string, RowState>();
  const ids = new Set(event.changes.map((change) => change.id));
  const nutritionRows = event.rows.filter((row) => row.isNutrition);
  const after = (column: string) =>
    Number(
      nutritionRows.find((row) => row.target.fields?.[0] === column)?.changes[0]?.record?.[column] ?? NaN,
    );

  for (const row of event.rows) {
    if (row.isCreation) {
      states.set(row.key, {
        blocked: true,
        reason:
          subject === 'food'
            ? 'Adding a food is not reverted here. Deactivate it from Details instead.'
            : 'Adding an exercise is not reverted here. Deactivate it from the Overview instead.',
      });
      continue;
    }
    const newer = newerTouch(row, history);
    if (newer !== null) {
      const target = newer.source.startsWith('revert:') ? newer.source.slice('revert:'.length) : null;
      const revertedBy = target !== null && (ids.has(target) || target === event.batchId);
      states.set(row.key, {
        blocked: true,
        reason: revertedBy
          ? `Reverted ${inlineWhen(newer.createdAt, now)}.`
          : `${sentenceCase(row.short)} changed since, ${inlineWhen(newer.createdAt, now)}. Revert that change first.`,
      });
      continue;
    }
    if (row.isNutrition && nutritionRows.length > 1) {
      const kcalColumn = row.target.fields?.[0] === 'kcal_per_100g';
      const macros =
        4 * (after('protein_g_per_100g') || 0) +
        4 * (after('carbs_g_per_100g') || 0) +
        9 * (after('fat_g_per_100g') || 0);
      states.set(row.key, {
        blocked: false,
        reason:
          kcalColumn && macros > 0
            ? `Reverting energy alone leaves macros at ${String(Math.round(macros))} kcal. Revert all ${String(nutritionRows.length)} instead.`
            : `Energy and macros revert together so they keep adding up. Revert all ${String(nutritionRows.length)} instead.`,
      });
      continue;
    }
    states.set(row.key, { blocked: false, reason: null });
  }
  return states;
}

/** The nutrition block of an event, when more than one of its figures moved and none has since. */
export function nutritionBlock(
  event: HistoryEvent,
  states: ReadonlyMap<string, RowState>,
): { count: number; target: RevertTarget } | null {
  const rows = event.rows.filter((row) => row.isNutrition);
  if (rows.length < 2) return null;
  if (rows.some((row) => states.get(row.key)?.blocked === true)) return null;
  const change = rows[0]?.changes[0];
  if (change === undefined) return null;
  return {
    count: rows.length,
    target: { changeId: change.id, fields: rows.flatMap((row) => row.target.fields ?? []) },
  };
}

/** Lines "Revert all" would put back: every one, when none is hard-blocked. */
export function revertAllCount(event: HistoryEvent, states: ReadonlyMap<string, RowState>): number {
  const open = event.rows.filter((row) => states.get(row.key)?.blocked !== true);
  return open.length > 1 && open.length === event.rows.length ? open.length : 0;
}

/**
 * Every name the food answered to before and after each event, worked back
 * from the names it has now: History records one alias row at a time, and an
 * operator reads the list.
 */
export function withNameLists(events: readonly HistoryEvent[], current: readonly string[]): HistoryEvent[] {
  let names = [...current];
  const list = (values: readonly string[]) => (values.length === 0 ? 'none' : values.join(', '));
  return events.map((event) => {
    const row = event.rows.find((candidate) => candidate.key.endsWith(':aliases'));
    if (row === undefined) return event;
    const added = row.changes.filter((c) => c.op === 'insert').map((c) => text(c.record?.alias));
    const removed = row.changes.filter((c) => c.op === 'delete').map((c) => text(c.oldRecord?.alias));
    const after = names;
    const before = [...after.filter((name) => !added.includes(name)), ...removed].sort((a, b) =>
      a.localeCompare(b),
    );
    names = before;
    return {
      ...event,
      rows: event.rows.map((candidate) =>
        candidate === row ? { ...row, before: list(before), after: list(after) } : candidate,
      ),
    };
  });
}
